<template>
    <div :style="wrapStyle">
        <v-avatar :size="px">
            <v-img v-if="photo" :src="photo" :alt="name" cover>
                <template #error>
                    <div :style="fallbackStyle">
                        <v-icon v-if="icon" color="white" :size="iconPx">{{ icon }}</v-icon>
                        <span v-else :style="textStyle">{{ ini }}</span>
                    </div>
                </template>
            </v-img>
            <div v-else :style="fallbackStyle">
                <v-icon v-if="icon" color="white" :size="iconPx">{{ icon }}</v-icon>
                <span v-else :style="textStyle">{{ ini }}</span>
            </div>
        </v-avatar>
        <span v-if="showStatus" :style="dotStyle"/>
    </div>
</template>

<script setup>
import {computed} from "vue";
import {avatarColor, initials} from "@/composables/appDesign";
import {statusMeta, subjectIcon, subjectKindOf, subjectStatusOf} from "@/composables/subjectKind";

// Type-aware avatar for a tracked subject. Shows the subject photo when set;
// otherwise a persona icon for non-person subjects (car/paw/bag) or first+last
// initials for people. An optional status dot mirrors the live status color.
// Mirrors the mobile app's subject_avatar widget.
const props = defineProps({
    device: {type: Object, required: true},
    size: {type: [Number, String], default: 40},
    showStatus: {type: Boolean, default: true},
});

const px = computed(() => {
    const n = Number(props.size);
    return Number.isFinite(n) ? n : 40;
});
const iconPx = computed(() => Math.round(px.value * 0.5));

const name = computed(() => props.device?.asset_label || props.device?.label
    || props.device?.slno || props.device?.name || "Tracker");
// A device linked to a circle member shows that member's photo, so all of a
// person's devices share one picture; otherwise the device's own photo.
const photo = computed(() => props.device?.member_photo || props.device?.asset_photo || props.device?.photo || "");
const ini = computed(() => initials(name.value));

// People show a photo or initials; other kinds show a persona/type icon.
const icon = computed(() => (subjectKindOf(props.device) === "person" ? null : subjectIcon(props.device)));
const bgColor = computed(() => avatarColor(name.value));
const dotColor = computed(() => statusMeta(subjectStatusOf(props.device)).color);

const wrapStyle = computed(() => ({
    position: "relative",
    display: "inline-block",
    width: `${px.value}px`,
    height: `${px.value}px`,
}));
const fallbackStyle = computed(() => ({
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: bgColor.value,
}));
const textStyle = computed(() => ({
    color: "#ffffff",
    fontWeight: 700,
    fontSize: `${Math.round(px.value * 0.38)}px`,
    lineHeight: 1,
}));
const dotStyle = computed(() => {
    const d = Math.max(8, Math.round(px.value * 0.28));
    return {
        position: "absolute",
        right: 0,
        bottom: 0,
        width: `${d}px`,
        height: `${d}px`,
        borderRadius: "50%",
        backgroundColor: dotColor.value,
        border: "2px solid #ffffff",
        boxSizing: "border-box",
    };
});
</script>
